import React from 'react';
import { Shield, MessageSquare, Database, Bug, Zap, BarChart3 } from 'lucide-react';
import { CERTIFICATIONS } from '../constants/data';

const About = () => {
  const interests = ['POV Strategy & Execution', 'Detection Engineering', 'Security Architecture', 'Technical Enablement', 'Fitness & Personal Development'];

  return (
    <div className="min-h-screen bg-black pt-16 relative overflow-hidden">
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-4 font-mono">
            <span className="text-green-400">&gt;</span> PROFILE.ANALYSIS
          </h1>
          <p className="text-gray-400 font-mono">[ PROFESSIONAL DATA & TECHNICAL SPECIFICATIONS ]</p>
        </div>
        
        {/* Current Role Section */}
        <div className="bg-gray-900/50 border border-green-500/30 rounded-xl p-8 backdrop-blur-sm mb-8">
          <h3 className="text-xl font-bold text-green-400 mb-4 font-mono flex items-center gap-2">
            <MessageSquare className="w-6 h-6" />
            CURRENT.ROLE
          </h3>
          <div className="text-gray-300 space-y-2">
            <p>
              <span className="text-blue-400 font-semibold">Senior Sales Engineer</span> at <span className="text-green-400 font-semibold">Vectra AI</span>
            </p>
            <p className="text-sm">
              Leading pre-sales engagements across Florida, North Carolina, and the Caribbean. Driving enterprise security transformations through strategic POV execution and technical solution design.
            </p>
            <p className="text-sm text-gray-400 mt-4 font-mono">
              💡 Available for: Technical partnerships • Conference speaking • POV collaboration • Security advisory
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          <div className="lg:col-span-2 bg-gray-900/50 border border-green-500/30 rounded-xl p-8 backdrop-blur-sm">
            <h2 className="text-2xl font-bold text-green-400 mb-6 font-mono flex items-center gap-2">
              <Shield className="w-6 h-6" />
              MISSION.PROFILE
            </h2>
            <div className="space-y-4 text-gray-300 leading-relaxed">
              {[
                { label: '[IDENTITY]', content: 'Senior Sales Engineer at Vectra AI, leading pre-sales engagements across Florida, North Carolina, and the Caribbean. Origin: Germany → Current Location: Miami, Florida.' },
                { label: '[MISSION]', content: 'Driving enterprise security transformations through strategic POV execution, technical solution design, and customer success. Expert at translating complex XDR/NDR/CDR capabilities into tangible business value for SOC teams and security leadership.' },
                { label: '[TRACK RECORD]', content: '7+ years in cybersecurity pre-sales with proven success: 90%+ POV win rate, consistent 110% quota attainment, and expertise across Vectra AI, Fortinet, and Cisco security portfolios.' },
                { label: '[EXPERTISE]', content: 'Specializing in XDR/NDR/CDR solutions, cloud security architecture, and SOC workflow optimization. OSCP and CRTO certified, bringing hands-on security expertise to customer engagements and solution design.' },
                { label: '[VALUE PROP]', content: 'Bridge between security vendors and enterprise customers. Fluent in both technical implementation and business strategy, ensuring successful security outcomes from POV through production deployment.' }
              ].map(({ label, content }) => (
                <p key={label}>
                  <span className="text-green-400 font-mono">{label}</span> {content}
                </p>
              ))}
            </div>
          </div>
          
          <div className="bg-gray-900/50 border border-blue-500/30 rounded-xl p-8 backdrop-blur-sm">
            <h3 className="text-lg font-bold text-blue-400 mb-6 font-mono flex items-center gap-2">
              <Database className="w-5 h-5" />
              SYSTEM.INFO
            </h3>
            <ul className="space-y-3 text-gray-300 font-mono">
              {[
                { icon: '●', color: 'green', text: 'Location: Miami, FL' },
                { icon: '●', color: 'blue', text: 'Origin: Germany' },
                { icon: '●', color: 'red', text: 'Role: Sr. Sales Engineer' },
                { icon: '●', color: 'yellow', text: 'Cert: OSCP | CRTO' },
                { icon: '●', color: 'purple', text: 'Languages: DE|EN|ES' },
                { icon: '●', color: 'green', text: 'Focus: Enterprise Sales' }
              ].map(({ icon, color, text }) => (
                <li key={text} className="flex items-center gap-2">
                  <span className={`text-${color}-400`}>{icon}</span> {text}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-gray-900/50 border border-red-500/30 rounded-xl p-8 backdrop-blur-sm">
            <h3 className="text-xl font-bold text-red-400 mb-6 font-mono flex items-center gap-2">
              <Bug className="w-6 h-6" />
              CREDENTIALS.DB
            </h3>
            <div className="space-y-4">
              {CERTIFICATIONS.map(({ title, org, year, color }) => (
                <div key={title} className={`border-l-4 border-${color}-500 pl-4`}>
                  <h4 className="font-bold text-white">{title}</h4>
                  <p className="text-gray-400 text-sm">{org} • {year}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-900/50 border border-purple-500/30 rounded-xl p-8 backdrop-blur-sm">
            <h3 className="text-xl font-bold text-purple-400 mb-6 font-mono flex items-center gap-2">
              <Zap className="w-6 h-6" />
              INTERESTS.LOG
            </h3>
            <div className="space-y-3">
              {interests.map((interest, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-green-400 font-mono">[{i + 1}]</span>
                  <span className="text-gray-300">{interest}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Experience Timeline */}
        <div className="mt-8 bg-gray-900/50 border border-green-500/30 rounded-xl p-8 backdrop-blur-sm">
          <h3 className="text-xl font-bold text-green-400 mb-6 font-mono flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            CAREER.TRAJECTORY
          </h3>
          <div className="space-y-4">
            <div className="border-l-4 border-green-500 pl-4">
              <h4 className="font-bold text-white">Vectra AI - Senior Sales Engineer</h4>
              <p className="text-gray-400 text-sm">Aug 2022 - Present • Miami, FL</p>
              <p className="text-gray-300 text-sm mt-1">90%+ POV win rate, leading enterprise security transformations</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-bold text-white">Fortinet - Systems Engineer</h4>
              <p className="text-gray-400 text-sm">Dec 2020 - Jul 2022 • Frankfurt, Germany</p>
              <p className="text-gray-300 text-sm mt-1">NGFW & SD-WAN solutions</p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="font-bold text-white">Cisco Systems - Systems Engineer</h4>
              <p className="text-gray-400 text-sm">Jul 2018 - Nov 2020 • Frankfurt, Germany</p>
              <p className="text-gray-300 text-sm mt-1">Enterprise Networking, ZTNA & SASE architecture</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;